import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Loader2, AlertTriangle, Sparkles, Check, X, ChevronDown, ChevronUp, Pencil } from "lucide-react";

function IngredientForm({ authAxios, onDone }) {
  const [name, setName] = useState(""); const [unit, setUnit] = useState("kg"); const [unitCost, setUnitCost] = useState(""); const [unitQty, setUnitQty] = useState("1"); const [supplier, setSupplier] = useState("");
  const save = async () => { if (!name || !unitCost) { toast.error("Nombre y costo requeridos"); return; } await authAxios.post("/intelligence/ingredients", { name, unit, unit_cost: parseFloat(unitCost), unit_quantity: parseFloat(unitQty) || 1, supplier }); toast.success("Ingrediente agregado"); onDone(); };
  return (
    <div className="bg-gray-50 rounded-xl p-4 space-y-3 border"><h4 className="font-bold text-sm">Novo Ingrediente</h4><div className="grid grid-cols-2 gap-2"><Input placeholder="Nome (ex: Salmão)" value={name} onChange={e => setName(e.target.value)} data-testid="ingredient-name" /><Input placeholder="Fornecedor" value={supplier} onChange={e => setSupplier(e.target.value)} /></div><div className="grid grid-cols-3 gap-2"><Input type="number" step="0.01" placeholder="Custo (EUR)" value={unitCost} onChange={e => setUnitCost(e.target.value)} data-testid="ingredient-cost" /><Input type="number" step="0.01" placeholder="Quantidade" value={unitQty} onChange={e => setUnitQty(e.target.value)} /><Select value={unit} onValueChange={setUnit}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent position="popper" sideOffset={4}><SelectItem value="kg">kg</SelectItem><SelectItem value="g">g</SelectItem><SelectItem value="L">L</SelectItem><SelectItem value="ml">ml</SelectItem><SelectItem value="und">unidade</SelectItem></SelectContent></Select></div><Button onClick={save} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-ingredient-btn">Guardar Ingrediente</Button></div>
  );
}

function RecipeForm({ authAxios, ingredients: availableIngredients, products, onDone }) {
  const [productId, setProductId] = useState(""); const [productName, setProductName] = useState(""); const [salePrice, setSalePrice] = useState("");
  const [packagingCost, setPackagingCost] = useState("0"); const [paymentFee, setPaymentFee] = useState("0"); const [marketplaceFee, setMarketplaceFee] = useState("0");
  const [recipeIngredients, setRecipeIngredients] = useState([]);
  const addIngredient = () => setRecipeIngredients([...recipeIngredients, { ingredient_id: "", name: "", quantity_used: 0, unit_cost: 0, unit_quantity: 1, unit: "kg" }]);
  const updateIng = (idx, field, value) => { setRecipeIngredients(prev => prev.map((ing, i) => { if (i !== idx) return ing; const updated = { ...ing, [field]: value }; if (field === "ingredient_id") { const found = availableIngredients.find(a => a.id === value); if (found) { updated.name = found.name; updated.unit_cost = found.unit_cost; updated.unit_quantity = found.unit_quantity; updated.unit = found.unit; } } return updated; })); };
  const handleSelectProduct = (pid) => { setProductId(pid); const p = products.find(x => x.id === pid); if (p) { setProductName(p.name); setSalePrice(String(p.price || "")); } };
  const totalCost = (() => { const ingCost = recipeIngredients.reduce((sum, i) => sum + (i.quantity_used / Math.max(i.unit_quantity, 0.001)) * i.unit_cost, 0); const pkg = parseFloat(packagingCost) || 0; const sp = parseFloat(salePrice) || 0; return ingCost + pkg + sp * (parseFloat(paymentFee) || 0) / 100 + sp * (parseFloat(marketplaceFee) || 0) / 100; })();
  const sp = parseFloat(salePrice) || 0; const cmvPct = sp > 0 ? ((totalCost / sp) * 100).toFixed(1) : 0; const marginPct = sp > 0 ? (100 - (totalCost / sp) * 100).toFixed(1) : 0;
  const save = async () => { if (!productName || !salePrice) { toast.error("Produto e preço requeridos"); return; } await authAxios.post("/intelligence/recipes", { product_id: productId, product_name: productName, sale_price: parseFloat(salePrice), packaging_cost: parseFloat(packagingCost) || 0, payment_fee_pct: parseFloat(paymentFee) || 0, marketplace_fee_pct: parseFloat(marketplaceFee) || 0, ingredients: recipeIngredients }); toast.success("Ficha técnica criada"); onDone(); };
  return (
    <div className="bg-gray-50 rounded-xl p-4 space-y-4 border"><h4 className="font-bold text-sm">Nova Ficha Técnica</h4>
      <div className="grid grid-cols-2 gap-2"><Select value={productId} onValueChange={handleSelectProduct}><SelectTrigger data-testid="recipe-product-select"><SelectValue placeholder="Selecionar produto..." /></SelectTrigger><SelectContent position="popper" sideOffset={4}>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.price}EUR)</SelectItem>)}</SelectContent></Select><Input type="number" step="0.01" placeholder="Preço venda (EUR)" value={salePrice} onChange={e => setSalePrice(e.target.value)} data-testid="recipe-price" /></div>
      <div><div className="flex items-center justify-between mb-2"><p className="text-xs font-bold text-gray-500 uppercase">Ingredientes</p><Button variant="outline" size="sm" onClick={addIngredient} className="gap-1 h-7 text-xs" data-testid="add-recipe-ingredient"><Plus className="w-3 h-3" /> Agregar</Button></div>{recipeIngredients.map((ing, idx) => (<div key={idx} className="flex gap-2 mb-2 items-center"><Select value={ing.ingredient_id} onValueChange={v => updateIng(idx, "ingredient_id", v)}><SelectTrigger className="flex-1"><SelectValue placeholder="Ingrediente..." /></SelectTrigger><SelectContent position="popper" sideOffset={4}>{availableIngredients.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.unit_cost}EUR/{a.unit_quantity}{a.unit})</SelectItem>)}</SelectContent></Select><Input type="number" step="0.001" placeholder="Qtd usada" className="w-24" value={ing.quantity_used || ""} onChange={e => updateIng(idx, "quantity_used", parseFloat(e.target.value) || 0)} /><span className="text-xs text-gray-400 w-8">{ing.unit}</span><Button variant="ghost" size="sm" className="text-red-500" onClick={() => setRecipeIngredients(prev => prev.filter((_, i) => i !== idx))}><Trash2 className="w-3 h-3" /></Button></div>))}</div>
      <div className="grid grid-cols-3 gap-2"><div><label className="text-xs text-gray-500">Embalagem EUR</label><Input type="number" step="0.01" value={packagingCost} onChange={e => setPackagingCost(e.target.value)} /></div><div><label className="text-xs text-gray-500">Taxa pagamento %</label><Input type="number" step="0.1" value={paymentFee} onChange={e => setPaymentFee(e.target.value)} /></div><div><label className="text-xs text-gray-500">Marketplace %</label><Input type="number" step="0.1" value={marketplaceFee} onChange={e => setMarketplaceFee(e.target.value)} /></div></div>
      <div className="bg-white rounded-lg p-3 border flex items-center justify-between"><div><p className="text-xs text-gray-400">Custo total</p><p className="text-lg font-bold">{totalCost.toFixed(2)}EUR</p></div><div className="text-center"><p className="text-xs text-gray-400">CMV</p><p className={`text-lg font-bold ${cmvPct > 35 ? "text-red-600" : "text-emerald-600"}`}>{cmvPct}%</p></div><div className="text-right"><p className="text-xs text-gray-400">Margem</p><p className={`text-lg font-bold ${marginPct < 65 ? "text-amber-600" : "text-emerald-600"}`}>{marginPct}%</p></div></div>
      <Button onClick={save} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-recipe-btn">Guardar Ficha Técnica</Button>
    </div>
  );
}

// Badge de origem do preço
function PriceBadge({ source }) {
  const map = {
    interno:     { cls: "bg-emerald-100 text-emerald-700", label: "Interno" },
    estoque:     { cls: "bg-blue-100 text-blue-700",       label: "Estoque" },
    manual:      { cls: "bg-gray-100 text-gray-600",       label: "Manual" },
    estimado_ia: { cls: "bg-amber-100 text-amber-700",     label: "Estimado IA" },
  };
  const { cls, label } = map[source] || map.manual;
  return <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0 ${cls}`}>{label}</span>;
}

// Modal de edição de ficha (preview ou existente)
function RecipeEditModal({ recipe, onSave, onClose }) {
  const [ingredients, setIngredients] = useState(() => (recipe.ingredients || []).map(i => ({ ...i })));
  const [packagingCost, setPackagingCost] = useState(String(recipe.packaging_cost ?? 0));
  const [paymentFee, setPaymentFee] = useState(String(recipe.payment_fee_pct ?? 0));
  const [marketplaceFee, setMarketplaceFee] = useState(String(recipe.marketplace_fee_pct ?? 0));
  const salePrice = Number(recipe.sale_price) || 0;

  const ingCost = ingredients.reduce((sum, i) =>
    sum + (Number(i.quantity_used) / Math.max(Number(i.unit_quantity) || 1, 0.001)) * Number(i.unit_cost || 0), 0);
  const pkg = parseFloat(packagingCost) || 0;
  const pFee = salePrice * (parseFloat(paymentFee) || 0) / 100;
  const mFee = salePrice * (parseFloat(marketplaceFee) || 0) / 100;
  const totalCost = ingCost + pkg + pFee + mFee;
  const cmvPct = salePrice > 0 ? (totalCost / salePrice * 100).toFixed(1) : 0;
  const marginPct = salePrice > 0 ? (100 - totalCost / salePrice * 100).toFixed(1) : 0;

  const updateIng = (idx, field, value) =>
    setIngredients(prev => prev.map((ing, i) =>
      i === idx ? { ...ing, [field]: value, price_source: ing.price_source === "interno" ? "interno" : "manual" } : ing
    ));
  const addIng = () => setIngredients(prev => [...prev, { name: "", quantity_used: 0, unit: "kg", unit_cost: 0, unit_quantity: 1, price_source: "manual" }]);
  const removeIng = (idx) => setIngredients(prev => prev.filter((_, i) => i !== idx));

  const handleSave = () => {
    const hasEstimated = ingredients.some(i => i.price_source === "estimado_ia");
    onSave({
      ...recipe,
      ingredients,
      packaging_cost: parseFloat(packagingCost) || 0,
      payment_fee_pct: parseFloat(paymentFee) || 0,
      marketplace_fee_pct: parseFloat(marketplaceFee) || 0,
      ingredients_cost: parseFloat(ingCost.toFixed(4)),
      total_cost: parseFloat(totalCost.toFixed(4)),
      cmv_pct: parseFloat(cmvPct),
      margin_pct: parseFloat(marginPct),
      has_estimated_cost: hasEstimated,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="bg-violet-700 px-5 py-4 flex items-center justify-between rounded-t-2xl shrink-0">
          <div>
            <h3 className="font-bold text-white text-sm">{recipe.product_name}</h3>
            <p className="text-xs text-violet-200">Preço venda: {salePrice.toFixed(2)}€</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-gray-500 uppercase">Ingredientes</p>
              <button onClick={addIng} className="text-xs text-violet-600 hover:text-violet-800 font-semibold flex items-center gap-1">
                <Plus className="w-3 h-3" /> Adicionar
              </button>
            </div>
            <div className="space-y-2">
              {ingredients.map((ing, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Input placeholder="Nome do ingrediente" value={ing.name}
                      onChange={e => updateIng(idx, "name", e.target.value)} className="flex-1 text-sm h-8" />
                    <PriceBadge source={ing.price_source} />
                    <button onClick={() => removeIng(idx)} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] text-gray-400">Qtd. usada</label>
                      <Input type="number" step="0.001" value={ing.quantity_used}
                        onChange={e => updateIng(idx, "quantity_used", parseFloat(e.target.value) || 0)} className="h-8 text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400">Unidade</label>
                      <Select value={ing.unit} onValueChange={v => updateIng(idx, "unit", v)}>
                        <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent position="popper" sideOffset={4}>
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="g">g</SelectItem>
                          <SelectItem value="L">L</SelectItem>
                          <SelectItem value="ml">ml</SelectItem>
                          <SelectItem value="und">unidade</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400">Custo/emb (€)</label>
                      <Input type="number" step="0.01" value={ing.unit_cost}
                        onChange={e => updateIng(idx, "unit_cost", parseFloat(e.target.value) || 0)} className="h-8 text-sm" />
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400">
                    Custo linha: {((Number(ing.quantity_used) / Math.max(Number(ing.unit_quantity) || 1, 0.001)) * Number(ing.unit_cost || 0)).toFixed(4)}€
                    {ing.unit_quantity > 1 && <span className="ml-1">(÷ {ing.unit_quantity} und/emb)</span>}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs text-gray-500">Embalagem (€)</label>
              <Input type="number" step="0.01" value={packagingCost} onChange={e => setPackagingCost(e.target.value)} className="h-8" /></div>
            <div><label className="text-xs text-gray-500">Taxa pagamento %</label>
              <Input type="number" step="0.1" value={paymentFee} onChange={e => setPaymentFee(e.target.value)} className="h-8" /></div>
            <div><label className="text-xs text-gray-500">Marketplace %</label>
              <Input type="number" step="0.1" value={marketplaceFee} onChange={e => setMarketplaceFee(e.target.value)} className="h-8" /></div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-3 gap-4 text-center">
            <div><p className="text-xs text-gray-400">Custo total</p><p className="text-lg font-bold text-gray-800">{totalCost.toFixed(2)}€</p></div>
            <div><p className="text-xs text-gray-400">CMV</p>
              <p className={`text-lg font-bold ${cmvPct > 35 ? "text-red-600" : "text-emerald-600"}`}>{cmvPct}%</p></div>
            <div><p className="text-xs text-gray-400">Margem</p>
              <p className={`text-lg font-bold ${marginPct < 65 ? "text-amber-600" : "text-emerald-600"}`}>{marginPct}%</p></div>
          </div>

          {ingredients.some(i => i.price_source === "estimado_ia") && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                <strong>CMV estimado</strong> — ingredientes marcados "Estimado IA" usam preço aproximado.
                Confirme os custos reais antes de aprovar.
              </p>
            </div>
          )}
        </div>

        <div className="p-4 border-t flex gap-3 justify-end shrink-0">
          <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
          <Button size="sm" className="bg-violet-600 hover:bg-violet-700" onClick={handleSave}>
            <Check className="w-3.5 h-3.5 mr-1.5" /> Guardar alterações
          </Button>
        </div>
      </div>
    </div>
  );
}

function AiPreviewPanel({ suggestions, onApprove, onCancel, saving }) {
  const [items, setItems] = useState(() => suggestions.map(s => ({ ...s, _status: "pending" })));
  const [editingRecipe, setEditingRecipe] = useState(null);

  const approved = items.filter(i => i._status === "approved");

  const setStatus = (id, status) =>
    setItems(prev => prev.map(i => i.id === id ? { ...i, _status: status } : i));

  const handleEditSave = (updated) => {
    setItems(prev => prev.map(i => i.id === updated.id ? { ...updated, _status: "approved" } : i));
    setEditingRecipe(null);
  };

  const handleSaveAll = () => {
    if (approved.length === 0) { toast.error("Nenhuma ficha aprovada."); return; }
    const toSave = approved.map(({ _status, ...r }) => r);
    onApprove(toSave);
  };

  return (
    <>
      {editingRecipe && <RecipeEditModal recipe={editingRecipe} onSave={handleEditSave} onClose={() => setEditingRecipe(null)} />}
      <div className="bg-white rounded-xl border-2 border-violet-200 shadow-md overflow-hidden" data-testid="ai-preview-panel">
        <div className="bg-violet-700 px-4 py-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-violet-200" />
          <h3 className="font-bold text-sm text-white flex-1">{items.length} fichas geradas — Revise, edite e aprove</h3>
          <span className="text-[10px] bg-white/20 text-white px-2 py-0.5 rounded-full">{approved.length}/{items.length} aprovadas</span>
        </div>

        <div className="divide-y max-h-[55vh] overflow-y-auto">
          {items.map(recipe => (
            <div key={recipe.id} className={`px-4 py-3 transition-colors ${recipe._status === "ignored" ? "bg-gray-50 opacity-50" : "bg-white"}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-gray-800">{recipe.product_name}</p>
                    {recipe.has_estimated_cost && (
                      <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5">
                        <AlertTriangle className="w-2.5 h-2.5" /> CMV estimado
                      </span>
                    )}
                    {recipe._status === "approved" && (
                      <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5">
                        <Check className="w-2.5 h-2.5" /> Aprovada
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {recipe.ingredients?.length || 0} ing · Custo: {Number(recipe.total_cost).toFixed(2)}€ · Preço: {Number(recipe.sale_price).toFixed(2)}€ ·{" "}
                    <span className={recipe.cmv_pct > 35 ? "text-red-600 font-semibold" : "text-emerald-600 font-semibold"}>CMV {recipe.cmv_pct}%</span>
                  </p>
                </div>
                <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
                  {recipe._status !== "ignored" && (
                    <>
                      <button onClick={() => setEditingRecipe(recipe)}
                        className="px-2.5 py-1 text-xs font-semibold border border-violet-200 text-violet-700 rounded-lg hover:bg-violet-50">
                        Ver/Editar
                      </button>
                      {recipe._status !== "approved"
                        ? <button onClick={() => setStatus(recipe.id, "approved")}
                            className="px-2.5 py-1 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Aprovar</button>
                        : <button onClick={() => setStatus(recipe.id, "pending")}
                            className="px-2.5 py-1 text-xs font-semibold bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300">Desfazer</button>
                      }
                      <button onClick={() => setStatus(recipe.id, "ignored")}
                        className="px-2.5 py-1 text-xs font-semibold text-gray-400 border border-gray-200 rounded-lg hover:bg-gray-50">Ignorar</button>
                    </>
                  )}
                  {recipe._status === "ignored" && (
                    <button onClick={() => setStatus(recipe.id, "pending")}
                      className="px-2.5 py-1 text-xs font-semibold text-gray-400 border border-gray-200 rounded-lg hover:bg-gray-50">Restaurar</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 bg-gray-50 border-t flex items-center justify-between gap-3">
          <div className="flex gap-3 text-xs font-semibold">
            <button onClick={() => setItems(prev => prev.map(i => ({ ...i, _status: "approved" })))} className="text-violet-600 hover:text-violet-800">Aprovar todas</button>
            <span className="text-gray-300">|</span>
            <button onClick={() => setItems(prev => prev.map(i => ({ ...i, _status: "ignored" })))} className="text-gray-400 hover:text-gray-600">Ignorar todas</button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onCancel} className="gap-1 text-gray-600">
              <X className="w-3 h-3" /> Cancelar
            </Button>
            <Button size="sm" disabled={approved.length === 0 || saving} onClick={handleSaveAll}
              className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-ai-recipes-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              {saving ? "A salvar..." : `Salvar ${approved.length} ficha${approved.length !== 1 ? "s" : ""}`}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export function CMVTab({ authAxios }) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [recipes, setRecipes] = useState([]); const [ingredients, setIngredients] = useState([]); const [cmvSummary, setCmvSummary] = useState(null); const [loading, setLoading] = useState(true);
  const [showRecipeForm, setShowRecipeForm] = useState(false); const [showIngredientForm, setShowIngredientForm] = useState(false); const [products, setProducts] = useState([]);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);
  const [aiSaving, setAiSaving] = useState(false);
  const [editingExistingRecipe, setEditingExistingRecipe] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => { setLoading(true); try { const [rec, ing, cmv, prod] = await Promise.all([authAxiosRef.current.get("/intelligence/recipes"), authAxiosRef.current.get("/intelligence/ingredients"), authAxiosRef.current.get("/intelligence/cmv"), authAxiosRef.current.get("/menu/products")]); setRecipes(rec.data); setIngredients(ing.data); setCmvSummary(cmv.data); setProducts(prod.data); } catch {} setLoading(false); }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAiGenerate = async (forceAll = false) => {
    setAiGenerating(true);
    try {
      const params = { preview: true };
      if (forceAll) params.force = true;
      const res = await authAxios.post("/intelligence/recipes/ai-bulk-generate", null, { params });
      if (res.data.preview) {
        if (res.data.suggestions?.length > 0) {
          setAiPreview(res.data.suggestions);
        } else if (!forceAll && res.data.skipped > 0) {
          // Todos já têm ficha — oferecer re-geração forçada
          const ok = window.confirm(`Todos os ${res.data.skipped} produtos já têm fichas técnicas.\n\nDeseja gerar sugestões alternativas para todos os produtos (para comparar/substituir)?`);
          if (ok) handleAiGenerate(true);
        } else {
          toast.info("Nenhum produto disponível para gerar fichas.");
        }
      } else {
        toast.error(res.data.message || "Erro inesperado ao gerar fichas.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro na geração com IA. Verifique a chave de API.");
    }
    setAiGenerating(false);
  };

  const handleAiApprove = async (approved) => {
    setAiSaving(true);
    try {
      const res = await authAxios.post("/intelligence/recipes/ai-bulk-save", { suggestions: approved });
      toast.success(res.data.message);
      setAiPreview(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar fichas");
    }
    setAiSaving(false);
  };

  const handleSaveExistingRecipe = async (updated) => {
    try {
      await authAxios.put(`/intelligence/recipes/${updated.id}`, updated);
      toast.success("Ficha atualizada");
      setEditingExistingRecipe(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar ficha");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl p-4 border shadow-sm"><p className="text-xs text-gray-400 uppercase">CMV Médio</p><p className={`text-2xl font-bold mt-1 ${(cmvSummary?.avg_cmv_pct || 0) > 35 ? "text-red-600" : "text-emerald-600"}`} data-testid="cmv-avg">{cmvSummary?.avg_cmv_pct || 0}%</p></div>
        <div className="bg-white rounded-xl p-4 border shadow-sm"><p className="text-xs text-gray-400 uppercase">Fichas Técnicas</p><p className="text-2xl font-bold text-gray-800 mt-1">{cmvSummary?.total_recipes || 0}</p></div>
        <div className="bg-white rounded-xl p-4 border shadow-sm"><p className="text-xs text-gray-400 uppercase">Ingredientes</p><p className="text-2xl font-bold text-gray-800 mt-1">{ingredients.length}</p></div>
      </div>
      {cmvSummary?.alerts?.length > 0 && <div className="bg-red-50 border border-red-200 rounded-xl p-4"><h4 className="text-sm font-bold text-red-700 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Alertas CMV</h4>{cmvSummary.alerts.map((a, i) => <p key={i} className="text-xs text-red-600 mt-1">{a}</p>)}</div>}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => setShowIngredientForm(!showIngredientForm)} className="gap-1" data-testid="add-ingredient-btn"><Plus className="w-3 h-3" /> Ingrediente</Button>
        <Button size="sm" onClick={() => setShowRecipeForm(!showRecipeForm)} className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="add-recipe-btn"><Plus className="w-3 h-3" /> Ficha Técnica</Button>
        <Button
          size="sm"
          onClick={handleAiGenerate}
          disabled={aiGenerating || !!aiPreview}
          className="gap-1 bg-violet-600 hover:bg-violet-700 text-white"
          data-testid="ai-generate-recipes-btn"
        >
          {aiGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {aiGenerating ? "A gerar com IA..." : "Gerar com IA"}
        </Button>
      </div>
      {showIngredientForm && <IngredientForm authAxios={authAxios} onDone={() => { setShowIngredientForm(false); fetchData(); }} />}
      {showRecipeForm && <RecipeForm authAxios={authAxios} ingredients={ingredients} products={products} onDone={() => { setShowRecipeForm(false); fetchData(); }} />}
      {editingExistingRecipe && (
        <RecipeEditModal recipe={editingExistingRecipe} onSave={handleSaveExistingRecipe} onClose={() => setEditingExistingRecipe(null)} />
      )}
      {aiPreview && (
        <AiPreviewPanel
          suggestions={aiPreview}
          saving={aiSaving}
          onApprove={handleAiApprove}
          onCancel={() => setAiPreview(null)}
        />
      )}
      {ingredients.length > 0 && <div className="bg-white rounded-xl border shadow-sm overflow-hidden"><div className="p-4 border-b"><h3 className="font-bold text-sm text-gray-700">Ingredientes</h3></div><div className="divide-y">{ingredients.map(ing => (<div key={ing.id} className="flex items-center px-4 py-2 hover:bg-gray-50"><div className="flex-1"><p className="text-sm font-medium">{ing.name}</p><p className="text-xs text-gray-400">{ing.supplier || "Sem fornecedor"}</p></div><p className="text-sm font-bold text-gray-800 mr-4">{ing.unit_cost?.toFixed(2)}EUR/{ing.unit_quantity}{ing.unit}</p><Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={async () => { await authAxios.delete(`/intelligence/ingredients/${ing.id}`); toast.success("Eliminado"); fetchData(); }}><Trash2 className="w-3 h-3" /></Button></div>))}</div></div>}
      {recipes.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="p-4 border-b"><h3 className="font-bold text-sm text-gray-700">Fichas Técnicas (CMV por produto)</h3></div>
          <div className="divide-y">
            {recipes.map(r => (
              <div key={r.id} className="flex items-center px-4 py-3 hover:bg-gray-50">
                <div className="flex-1">
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    {r.product_name}
                    {r.has_estimated_cost && (
                      <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">CMV estimado</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400 flex items-center gap-1.5">
                    {r.ingredients?.length || 0} ingredientes · Emb: {r.packaging_cost?.toFixed(2)}EUR
                    {r.ai_generated && <span className="inline-flex items-center gap-0.5 text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded font-semibold"><Sparkles className="w-2.5 h-2.5" />IA</span>}
                  </p>
                </div>
                <div className="text-right mr-4"><p className="text-xs text-gray-400">Custo: {r.total_cost?.toFixed(2)}EUR</p><p className="text-xs text-gray-400">Preço: {r.sale_price?.toFixed(2)}EUR</p></div>
                <div className="text-right mr-2"><p className={`text-sm font-bold ${r.cmv_pct > 35 ? "text-red-600" : "text-emerald-600"}`}>CMV {r.cmv_pct}%</p><p className="text-xs text-gray-400">Margem {r.margin_pct}%</p></div>
                <Button variant="ghost" size="sm" className="text-violet-500 hover:text-violet-700 mr-1" onClick={() => setEditingExistingRecipe(r)} title="Editar ficha"><Pencil className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" className="text-red-500" onClick={async () => { await authAxios.delete(`/intelligence/recipes/${r.id}`); toast.success("Eliminado"); fetchData(); }}><Trash2 className="w-3 h-3" /></Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
